// src/store/game.js

import { defineStore } from "pinia";
import api from "../services/api";
import cityMapData from '@/data/cityMap.json';
import heroMapData from '@/data/heroMap.json';
import cardsData from '@/data/cards.json';
import { useAuthStore } from "./auth";
import { snapshotToBaseStats } from "../utils/cardStats";
import { abilitiesForRarity } from "../utils/heroAbilities";
import { buildHeroCombatSheet } from "../utils/heroCombatStats";
import { UNIT_SLUGS, UNIT_REQUIRES_NODE, unitDisplay, emptyUnitMap, normalizeUnitMap, unitBuildingLockState, cityNodeDisplayName } from "../utils/unitDisplay";
import {
  cloneCells,
  findCell,
  parseCellsPayload,
  parseFormationPayload,
  parseGateCol,
  parseGarrisonHero,
  remainingBySlug,
  sliderMaxForSlug,
  sortCells,
  placeStack,
  withoutCell,
  isPlaceable,
  DEFAULT_GATE_COL,
  GATE_ROW,
} from "../utils/defenseFormation";
import {
  placeAttackStack,
  peelAttackStack,
  subtractUnitMaps,
  isAttackerCell,
} from "../utils/battleGrid";
import { useUiStore } from "./ui";

const RESOURCE_RATE_KEYS = [
  "gold",
  "food",
  "wood",
  "stone",
  "iron",
  "essence",
  "gems",
  "population",
];

function emptyRateEntry() {
  return { net: 0, produces: 0, consumes: 0 };
}

function emptyNestedRates() {
  return Object.fromEntries(
    RESOURCE_RATE_KEYS.map((key) => [key, emptyRateEntry()])
  );
}

function normalizeRateEntry(value) {
  if (value && typeof value === "object" && "net" in value) {
    return {
      net: Number.isFinite(Number(value.net)) ? Number(value.net) : 0,
      produces: Number(value.produces) || 0,
      consumes: Number(value.consumes) || 0,
    };
  }
  const n = Number(value) || 0;
  return {
    net: n,
    produces: n > 0 ? n : 0,
    consumes: n < 0 ? -n : 0,
  };
}

function mergeNestedRates(base, incoming) {
  const next = emptyNestedRates();
  for (const key of RESOURCE_RATE_KEYS) {
    next[key] = normalizeRateEntry(incoming?.[key] ?? base?.[key]);
  }
  return next;
}

function emptyAggregation() {
  return {
    gold: 0,
    food: 0,
    wood: 0,
    stone: 0,
    iron: 0,
    essence: 0,
    gems: 0,
    population: 0,
    last_ticked_at: null,
    protected_until: null,
    shield_kind: null,
    rates: emptyNestedRates(),
  };
}

const CLAIM_COOLDOWN_FLOOR_MS = 5000;

function tickStampMs(value) {
  const n = new Date(value).getTime();
  return Number.isFinite(n) ? n : null;
}

// Normalize JSON nodes to the shape expected by ProgressionMap.vue
function normalizeMapData(rawMap) {
  const map = JSON.parse(JSON.stringify(rawMap));
  map.nodes = map.nodes.map(node => ({
    ...node,
    id: node.node_key,
    position: { x: node.pos_x, y: node.pos_y },
  }));
  return map;
}

function newestReportFirst(a, b) {
  const ta = new Date(a?.created_at).getTime();
  const tb = new Date(b?.created_at).getTime();
  return (Number.isFinite(tb) ? tb : 0) - (Number.isFinite(ta) ? ta : 0);
}

function paperHasGridIntel(paper) {
  if (!paper || typeof paper.formation !== "object" || paper.formation == null) {
    return false;
  }
  return Array.isArray(paper.revealed) && paper.revealed.length > 0;
}

function buildBattleTargets(reports, empires) {
  const byUser = new Map();
  for (const report of reports || []) {
    if (report?.kind === "defense_report") continue;
    const id = Number(report.other_user_id);
    if (!Number.isFinite(id)) continue;
    if (!byUser.has(id)) byUser.set(id, []);
    byUser.get(id).push(report);
  }

  const peerById = new Map();
  for (const empire of empires || []) {
    peerById.set(Number(empire.id), empire);
  }

  const intel = [];
  const counter = [];
  const claimed = new Set();

  for (const [userId, papers] of byUser) {
    claimed.add(userId);
    const peer = peerById.get(userId);
    const sorted = [...papers].sort(newestReportFirst);
    const latest = sorted[0];
    const scouts = sorted.filter((paper) => paper.kind === "scout_intel");
    if (latest?.kind === "counter_intel" && paperHasGridIntel(latest)) {
      counter.push({
        id: userId,
        bucket: "counter",
        nickname: latest.other_nickname || peer?.nickname || "Unknown Empire",
        race: latest.other_race || peer?.race || "Unknown",
        cityName: latest.city_name || peer?.cityName || "Unknown Location",
        formation: parseFormationPayload(latest.formation),
        revealed: Array.isArray(latest.revealed) ? latest.revealed : [],
        fogAll: false,
        protected_until: peer?.protected_until || null,
      });
    } else if (scouts.length) {
      const scout = scouts[0];
      intel.push({
        id: userId,
        bucket: "intel",
        nickname: scout.other_nickname || peer?.nickname || "Unknown Empire",
        race: scout.other_race || peer?.race || "Unknown",
        cityName: scout.city_name || peer?.cityName || "Unknown Location",
        formation: parseFormationPayload(scout.formation),
        revealed: Array.isArray(scout.revealed) ? scout.revealed : [],
        fogAll: false,
        protected_until: peer?.protected_until || null,
      });
    } else {
      counter.push({
        id: userId,
        bucket: "counter",
        nickname: latest.other_nickname || peer?.nickname || "Unknown Empire",
        race: latest.other_race || peer?.race || "Unknown",
        cityName: peer?.cityName || "Unknown Location",
        formation: { gate_col: DEFAULT_GATE_COL, cells: [] },
        revealed: [],
        fogAll: true,
        protected_until: peer?.protected_until || null,
      });
    }
  }

  const blind = [];
  for (const empire of empires || []) {
    if (empire.isSelf) continue;
    const id = Number(empire.id);
    if (!Number.isFinite(id) || claimed.has(id)) continue;
    blind.push({
      id,
      bucket: "blind",
      nickname: empire.nickname || "Unknown Empire",
      race: empire.race || "Unknown",
      cityName: empire.cityName || "Unknown Location",
      formation: { gate_col: DEFAULT_GATE_COL, cells: [] },
      revealed: [],
      fogAll: true,
      protected_until: empire.protected_until || null,
    });
  }

  return { intel, counter, blind };
}

export const useGameStore = defineStore("game", {
  state: () => ({
    playerHeroes: [],
    playerCities: [],
    activeHero: null,
    activeCity: null,
    heroProgression: null,
    cityProgression: null,
    isLoading: false,
    cities: [],
    globalAggregation: emptyAggregation(),
    empireNetWorth: null,
    tickIntervalMs: 60 * 60 * 1000,
    serverTime: null,
    clockOffsetMs: 0,
    isClaiming: false,
    claimCooldownUntil: 0,
    availableQuests: [],
    activeQuests: [],
    questAssignments: {},
    focusedQuestSlug: null,
    justActivatedHero: false,
    justFoundedCity: false,
    playerCards: [],
    playerPacks: [],
    playerUnits: emptyUnitMap(),
    garrisonAssigned: emptyUnitMap(),
    citySockets: [],
    cityDefenseCells: [],
    cityDefenseGateCol: DEFAULT_GATE_COL,
    cityDefenseGarrisonHero: null,
    defenseDraftCells: [],
    defenseDraftGateCol: DEFAULT_GATE_COL,
    defenseGarrisonHero: null,
    isMovingGate: false,
    defenseSelectedSlug: null,
    defenseSelectedHeroId: null,
    defensePlaceQty: 1,
    isSavingDefense: false,
    defenseSaveError: "",
    heroSockets: [],
    marketPrices: [],
    worldMap: [],
    isWorldMapLoading: false,
    worldMapError: null,
    scoutingMissions: [],
    intelReports: [],
    selectedWorldEmpire: null,
    unreadIntelCount: 0,
    isScouting: false,
    scoutError: "",
    travelSecondsPerHex: 60,
    selectedBattleTargetId: null,
    battleAttackCells: [],
    battleSelectedSlug: null,
    battlePlaceQty: 1,
    battleSelectedHeroId: null,
    battleDeployedHero: null,
    isStartingBattle: false,
    battleStartError: "",
  }),

  // Add the new getters for panel visibility
  getters: {
    userRace: (state) => {
      const authStore = useAuthStore();
      return authStore.user?.race || 'eldorians';
    },
    // Map JSON labels are keyed by display name (e.g. Eldorians), not slug
    raceLabelKey: () => {
      const authStore = useAuthStore();
      const slug = authStore.user?.race;
      if (!slug) return "Eldorians";
      const fromList = authStore.races?.find((r) => r.slug === slug);
      if (fromList?.name) return fromList.name;
      return slug.charAt(0).toUpperCase() + slug.slice(1);
    },
    isHeroPanelEnabled: (state) => state.activeHero !== null,
    isCityPanelEnabled: (state) => state.activeCity !== null,
    isQuestPanelEnabled: (state) => state.activeHero !== null,
    scoutReserve: (state) =>
      Math.max(0, Math.floor(Number(state.playerUnits?.scout) || 0)),
    activeCityEconomy: (state) => {
      const id = state.activeCity?.id;
      if (id == null) return null;
      return (
        state.cities.find((city) => String(city.id) === String(id)) || null
      );
    },
    activeCityRates: (state) => {
      const id = state.activeCity?.id;
      if (id == null) return emptyNestedRates();
      const city = state.cities.find((row) => String(row.id) === String(id));
      return city?.rates ? mergeNestedRates(emptyNestedRates(), city.rates) : emptyNestedRates();
    },
    unlockedCityNodeKeys: (state) => {
      const nodes = state.cityProgression?.nodes || [];
      return nodes
        .filter((node) => node.isUnlocked)
        .map((node) => node.node_key || node.id)
        .filter(Boolean);
    },
    activeCityArmy() {
      const yields = this.activeCityEconomy?.unitYields || {};
      const reserves = this.playerUnits || emptyUnitMap();
      const race = this.userRace;
      const unlockedNodes = this.unlockedCityNodeKeys;
      const nodeLabels = Object.fromEntries(
        Object.keys(UNIT_REQUIRES_NODE)
          .map((slug) => UNIT_REQUIRES_NODE[slug])
          .filter((nodeKey, index, list) => list.indexOf(nodeKey) === index)
          .map((nodeKey) => [
            nodeKey,
            cityNodeDisplayName(
              nodeKey,
              this.raceLabelKey,
              this.cityProgression?.nodes?.length
                ? this.cityProgression.nodes
                : cityMapData[0]?.nodes || []
            ),
          ])
      );
      return UNIT_SLUGS.map((slug) => {
        const display = unitDisplay(slug, race);
        const lock = unitBuildingLockState(slug, { unlockedNodes, nodeLabels });
        return {
          slug,
          name: display.name,
          description: display.description,
          icon: display.icon,
          color: display.color,
          hp: display.hp,
          atk: display.atk,
          def: display.def,
          reserve: Math.max(0, Math.floor(Number(reserves[slug]) || 0)),
          yield: Math.max(0, Math.floor(Number(yields[slug]) || 0)),
          locked: lock.locked,
          lockReasons: lock.reasons,
        };
      });
    },
    activeCityBarracksRoster() {
      return this.activeCityArmy || [];
    },
    defenseRemaining() {
      return remainingBySlug(this.defenseDraftCells, this.playerUnits);
    },
    defenseSliderMax() {
      return sliderMaxForSlug(
        this.defenseDraftCells,
        this.playerUnits,
        this.defenseSelectedSlug
      );
    },
    defenseCellMap() {
      const map = {};
      for (const cell of this.defenseDraftCells || []) {
        map[`${cell.row},${cell.col}`] = cell;
      }
      return map;
    },
    battleTargets() {
      return buildBattleTargets(this.intelReports, this.worldMap);
    },
    selectedBattleTarget() {
      const id = Number(this.selectedBattleTargetId);
      if (!Number.isFinite(id)) return null;
      const groups = this.battleTargets;
      return (
        [...groups.intel, ...groups.counter, ...groups.blind].find(
          (target) => Number(target.id) === id
        ) || null
      );
    },
    battleIdleUnits() {
      const planned = emptyUnitMap();
      for (const roster of this.plannedRosters || []) {
        const troops = roster.assigned_troops || {};
        for (const slug of UNIT_SLUGS) {
          planned[slug] += Math.max(0, Math.floor(Number(troops[slug]) || 0));
        }
      }
      return subtractUnitMaps(this.playerUnits, this.garrisonAssigned, planned);
    },
    battleRemaining() {
      return remainingBySlug(this.battleAttackCells, this.battleIdleUnits);
    },
    battleSliderMax() {
      return sliderMaxForSlug(
        this.battleAttackCells,
        this.battleIdleUnits,
        this.battleSelectedSlug
      );
    },
    spendableResources: (state) => {
      const agg = state.globalAggregation || emptyAggregation();
      const id = state.activeCity?.id;
      const city =
        id == null
          ? null
          : state.cities.find((row) => String(row.id) === String(id));
      return {
        gold: Number(agg.gold) || 0,
        food: Number(agg.food) || 0,
        wood: Number(agg.wood) || 0,
        stone: Number(agg.stone) || 0,
        iron: Number(agg.iron) || 0,
        essence: Number(agg.essence) || 0,
        gems: Number(agg.gems) || 0,
        population: Number(city?.population) || 0,
      };
    },
    rateNet: (state) => (key) => {
      const entry = state.globalAggregation?.rates?.[key];
      if (entry && typeof entry === "object") {
        const n = Number(entry.net);
        return Number.isFinite(n) ? n : 0;
      }
      const n = Number(entry);
      return Number.isFinite(n) ? n : 0;
    },
    msUntilNextTick(state) {
      const interval = Number(state.tickIntervalMs) || 60 * 60 * 1000;
      const lastMs = new Date(state.globalAggregation?.last_ticked_at).getTime();
      if (!Number.isFinite(lastMs)) return interval;
      const elapsed = Date.now() + (Number(state.clockOffsetMs) || 0) - lastMs;
      return Math.max(0, interval - elapsed);
    },
    isTickDue() {
      return this.msUntilNextTick <= 0;
    },
    assignmentForQuest: (state) => (slug) => {
      return (
        state.questAssignments[slug] || {
          assigned_hero_id: null,
          assigned_troops: {},
        }
      );
    },
    focusedQuest(state) {
      const slug = state.focusedQuestSlug;
      if (!slug) return null;
      return (
        state.availableQuests.find((q) => q.slug === slug) ||
        state.activeQuests.find((q) => (q.slug || q.quest_slug) === slug) ||
        null
      );
    },
    remainingUnitsForQuest: (state) => (questSlug) => {
      const remaining = normalizeUnitMap(state.playerUnits);
      for (const [slug, assignment] of Object.entries(state.questAssignments)) {
        if (slug === questSlug) continue;
        const troops = assignment?.assigned_troops || {};
        for (const unitSlug of UNIT_SLUGS) {
          remaining[unitSlug] = Math.max(
            0,
            remaining[unitSlug] - (Math.floor(Number(troops[unitSlug]) || 0))
          );
        }
      }
      return remaining;
    },
    inProgressHeroIds: (state) => {
      const ids = new Set();
      for (const quest of state.activeQuests) {
        if (quest.assigned_hero_id) ids.add(quest.assigned_hero_id);
      }
      return ids;
    },
    busyHeroIds: (state) => {
      const ids = new Set();
      for (const quest of state.activeQuests) {
        if (quest.assigned_hero_id) ids.add(quest.assigned_hero_id);
      }
      for (const assignment of Object.values(state.questAssignments)) {
        if (assignment?.assigned_hero_id) ids.add(assignment.assigned_hero_id);
      }
      return ids;
    },
    plannedRosters: (state) => {
      return Object.entries(state.questAssignments)
        .filter(([, assignment]) => {
          if (assignment?.assigned_hero_id) return true;
          const troops = assignment?.assigned_troops || {};
          return UNIT_SLUGS.some((unitSlug) => (Number(troops[unitSlug]) || 0) > 0);
        })
        .map(([slug, assignment]) => {
          const quest = state.availableQuests.find((q) => q.slug === slug);
          const hero = state.playerHeroes.find(
            (h) => h.id === assignment.assigned_hero_id
          );
          return {
            slug,
            title: quest?.title || slug,
            hero,
            assigned_troops: assignment.assigned_troops || {},
          };
        });
    },
    ownedCards: (state) => {
      const catalog = Array.isArray(cardsData) ? cardsData : [];
      const bySlug = Object.fromEntries(catalog.map((card) => [card.slug, card]));
      return state.playerCards.map((owned) => {
        const def = bySlug[owned.card_slug] || {};
        const instance = owned.instance_data || {};
        const isEquipment = def.type === "equipment";
        const isSiege = def.sub_type === "gate";
        const instanceStats =
          (isEquipment || isSiege) &&
          instance.stats &&
          typeof instance.stats === "object"
            ? instance.stats
            : null;
        return {
          id: owned.id,
          slug: owned.card_slug,
          quantity: owned.quantity || 1,
          name: def.name || owned.card_slug,
          type: def.type || "",
          set: def.set || "",
          equipment_set: def.equipment_set || "",
          rarity: instance.rarity || "common",
          sub_type: def.sub_type || def.sub_type || "",
          description: def.description || "",
          image: def.image || def.image || "",
          image_position: def.image_position || null,
          legend: def.legend || def.legend || "",
          stats: instanceStats || def.stats || {},
          affixes: isEquipment || isSiege
            ? instance.affixes || def.affixes || []
            : def.affixes || [],
          abilities: abilitiesForRarity(
            instance.abilities || def.abilities,
            instance.rarity
          ),
          base_stats: snapshotToBaseStats(instance),
          instance_data: instance,
        };
      });
    },
    socketableCards() {
      return (node) => {
        const nodeKey = node?.node_key || node?.id;
        const isHeroRoot = nodeKey === "Hero";
        const socketType = node?.socket_type;
        if (!isHeroRoot && !socketType) return [];
        const allSockets = [
          ...(this.citySockets || []),
          ...(this.heroSockets || []),
        ];
        const occupiedIds = new Set(
          allSockets.map((row) => row.player_card_id)
        );
        const equippedHere = allSockets.find((row) => row.node_key === nodeKey);
        return this.ownedCards.filter((card) => {
          if (isHeroRoot) {
            if (card.type !== "hero") return false;
          } else if (card.sub_type !== socketType) {
            return false;
          }
          if (equippedHere && card.id === equippedHere.player_card_id) return false;
          if (card.instance_data?.in_use) return false;
          if (occupiedIds.has(card.id)) return false;
          return true;
        });
      };
    },
    socketedHeroCard() {
      return (heroId) => {
        if (!heroId) return null;
        const socket = (this.heroSockets || []).find(
          (row) =>
            String(row.player_hero_id) === String(heroId) &&
            row.node_key === "Hero"
        );
        if (!socket) return null;
        return (
          this.ownedCards.find((card) => card.id === socket.player_card_id) ||
          null
        );
      };
    },
    socketArtByNodeKey() {
      const byId = Object.fromEntries(
        this.ownedCards.map((card) => [card.id, card])
      );
      const map = {};
      for (const row of [
        ...(this.citySockets || []),
        ...(this.heroSockets || []),
      ]) {
        const card = byId[row.player_card_id];
        if (!card?.image) continue;
        map[row.node_key] = {
          image: card.image,
          name: card.name,
          rarity: card.rarity || "common",
        };
      }
      return map;
    },
    activeHeroCombatSheet() {
      const hero = this.activeHero;
      const ownedCards = this.ownedCards;
      const heroSockets = this.heroSockets || [];
      const nodes = this.heroProgression?.nodes || [];
      const heroCard = hero ? this.socketedHeroCard(hero.id) : null;
      return buildHeroCombatSheet({
        hero,
        heroCard,
        ownedCards,
        heroSockets,
        nodes,
        raceLabelKey: this.raceLabelKey,
      });
    },
  },

  actions: {
    applyResourcePayload(payload) {
      if (!payload) return;
      const envelope =
        Array.isArray(payload.cities) || payload.globalAggregation
          ? payload
          : payload.resources &&
            (Array.isArray(payload.resources.cities) ||
              payload.resources.globalAggregation)
          ? payload.resources
          : null;
      if (!envelope) return;

      const { cities, globalAggregation, tickIntervalMs, serverTime, units, garrisonAssigned } = envelope;
      if (Array.isArray(cities)) {
        this.cities = cities;
      }
      if (units && typeof units === "object") {
        this.playerUnits = normalizeUnitMap(units);
      }
      if (garrisonAssigned && typeof garrisonAssigned === "object") {
        this.garrisonAssigned = normalizeUnitMap(garrisonAssigned);
      }
      if (globalAggregation && typeof globalAggregation === "object") {
        this.globalAggregation = {
          ...emptyAggregation(),
          ...globalAggregation,
          rates: mergeNestedRates(
            emptyNestedRates(),
            globalAggregation.rates
          ),
        };
      } else if (Array.isArray(cities)) {
        this.globalAggregation = emptyAggregation();
      }
      if (tickIntervalMs) {
        this.tickIntervalMs = tickIntervalMs;
      }
      if (serverTime) {
        this.serverTime = serverTime;
        const parsed = Date.parse(serverTime);
        if (Number.isFinite(parsed)) {
          this.clockOffsetMs = parsed - Date.now();
        }
      }
      const worthRaw = envelope.empire_net_worth ?? payload.empire_net_worth;
      if (worthRaw != null) {
        const worth = Number(worthRaw);
        if (Number.isFinite(worth)) {
          this.empireNetWorth = worth;
          const authStore = useAuthStore();
          if (authStore.user) {
            authStore.user.empire_net_worth = worth;
          }
        }
      }
    },

    // Fetches core assets like heroes and cities
    async fetchPlayerAssets({ force = false } = {}) {
      if (
        !force &&
        (this.playerCities.length > 0 || this.playerHeroes.length > 0)
      )
        return;
      this.isLoading = true;
      try {
        const previousHeroId = this.activeHero?.id;
        const previousCityId = this.activeCity?.id;
        const [heroesRes, citiesRes] = await Promise.all([
          api.get("/api/player/heroes"),
          api.get("/api/player/cities"),
        ]);
        this.playerHeroes = heroesRes.data;
        this.playerCities = citiesRes.data;
        if (this.playerHeroes.length > 0) {
          this.activeHero =
            this.playerHeroes.find((h) => h.id === previousHeroId) ||
            this.playerHeroes[0];
        }
        if (this.playerCities.length > 0) {
          this.activeCity =
            this.playerCities.find((c) => c.id === previousCityId) ||
            this.playerCities[0];
        }
      } catch (error) {
        console.error("Failed to fetch player assets:", error);
      } finally {
        this.isLoading = false;
      }
    },

    async fetchPlayerCards() {
      try {
        const response = await api.get("/api/player/cards");
        this.playerCards = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        console.error("Failed to fetch player cards:", error);
        this.playerCards = [];
      }
    },

    async fetchPlayerPacks() {
      try {
        const response = await api.get("/api/player/packs");
        this.playerPacks = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        console.error("Failed to fetch player packs:", error);
        this.playerPacks = [];
      }
    },

    async openPack(packId) {
      const response = await api.post("/api/player/packs/open", { packId });
      const granted = Array.isArray(response.data?.granted)
        ? response.data.granted
        : [];
      await Promise.all([this.fetchPlayerPacks(), this.fetchPlayerCards()]);
      return granted;
    },

    mergeEmpireStocks(resourcesRow) {
      if (!resourcesRow || typeof resourcesRow !== "object") return;
      const stocks = {};
      for (const key of ["gold", "food", "wood", "stone", "iron", "essence", "gems"]) {
        if (resourcesRow[key] == null) continue;
        const n = Number(resourcesRow[key]);
        stocks[key] = Number.isFinite(n) ? n : 0;
      }
      if (!Object.keys(stocks).length) return;
      this.globalAggregation = {
        ...this.globalAggregation,
        ...stocks,
      };
    },

    async destroyCard(cardId) {
      const response = await api.post(`/api/player/cards/${cardId}/destroy`);
      this.playerCards = this.playerCards.filter(
        (card) => Number(card.id) !== Number(cardId)
      );
      this.mergeEmpireStocks(response.data?.resources);
      return response.data;
    },

    async acknowledgeNewCard(cardId) {
      const id = Number(cardId);
      if (!Number.isInteger(id) || id <= 0) return null;
      const owned = this.playerCards.find((card) => Number(card.id) === id);
      if (!owned?.instance_data?.is_new) return owned || null;
      try {
        const response = await api.post(`/api/player/cards/${id}/seen`);
        const next = response.data?.card;
        this.playerCards = this.playerCards.map((card) => {
          if (Number(card.id) !== id) return card;
          if (next) return next;
          const instance = { ...(card.instance_data || {}) };
          delete instance.is_new;
          return { ...card, instance_data: instance };
        });
        return next || null;
      } catch (error) {
        console.error(
          "Failed to acknowledge new card:",
          error.response?.data || error
        );
        return null;
      }
    },

    async fetchCitySockets(cityId) {
      if (!cityId) {
        this.citySockets = [];
        return [];
      }
      try {
        const response = await api.get(`/api/progression/city/${cityId}/sockets`);
        this.citySockets = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        console.error("Failed to fetch city sockets:", error);
        this.citySockets = [];
      }
      return this.citySockets;
    },

    clampDefensePlaceQty() {
      const max = Number(this.defenseSliderMax) || 0;
      if (max < 1) {
        this.defensePlaceQty = 0;
        return;
      }
      const n = Math.floor(Number(this.defensePlaceQty) || 0);
      this.defensePlaceQty = Math.min(max, Math.max(1, n));
    },

    setDefenseSelectedSlug(slug) {
      const key = slug ? String(slug).toLowerCase() : null;
      this.defenseSelectedSlug = key && UNIT_SLUGS.includes(key) ? key : null;
      if (this.defenseSelectedSlug) this.defenseSelectedHeroId = null;
      this.clampDefensePlaceQty();
    },

    setDefenseSelectedHeroId(heroId) {
      const n = Math.floor(Number(heroId));
      if (!Number.isFinite(n) || n < 1) {
        this.defenseSelectedHeroId = null;
        return;
      }
      this.defenseSelectedHeroId =
        this.defenseSelectedHeroId === n ? null : n;
      if (this.defenseSelectedHeroId) this.defenseSelectedSlug = null;
    },

    setDefensePlaceQty(qty) {
      this.defensePlaceQty = Math.floor(Number(qty) || 0);
      this.clampDefensePlaceQty();
    },

    startDefenseEdit() {
      const uiStore = useUiStore();
      this.defenseDraftCells = cloneCells(this.cityDefenseCells);
      this.defenseDraftGateCol = parseGateCol(this.cityDefenseGateCol);
      this.defenseGarrisonHero = this.cityDefenseGarrisonHero
        ? { ...this.cityDefenseGarrisonHero }
        : null;
      this.isMovingGate = false;
      this.defenseSelectedSlug = null;
      this.defenseSelectedHeroId = null;
      this.defensePlaceQty = 1;
      this.defenseSaveError = "";
      uiStore.setEditingDefense(true);
    },

    cancelDefenseEdit() {
      const uiStore = useUiStore();
      this.defenseDraftCells = cloneCells(this.cityDefenseCells);
      this.defenseDraftGateCol = parseGateCol(this.cityDefenseGateCol);
      this.defenseGarrisonHero = this.cityDefenseGarrisonHero
        ? { ...this.cityDefenseGarrisonHero }
        : null;
      this.isMovingGate = false;
      this.defenseSelectedSlug = null;
      this.defenseSelectedHeroId = null;
      this.defensePlaceQty = 1;
      this.defenseSaveError = "";
      uiStore.setEditingDefense(false);
    },

    moveDefenseGate(col) {
      const nextCol = parseGateCol(col);
      this.defenseDraftGateCol = nextCol;
      this.defenseDraftCells = withoutCell(
        this.defenseDraftCells,
        GATE_ROW,
        nextCol
      );
      if (
        this.defenseGarrisonHero &&
        this.defenseGarrisonHero.row === GATE_ROW &&
        this.defenseGarrisonHero.col === nextCol
      ) {
        this.defenseGarrisonHero = null;
      }
      this.clampDefensePlaceQty();
    },

    placeDefenseStack(row, col) {
      const r = Math.floor(Number(row));
      const c = Math.floor(Number(col));
      if (!isPlaceable(r, c, this.defenseDraftGateCol)) return false;

      if (this.defenseSelectedHeroId) {
        const sameCell =
          this.defenseGarrisonHero &&
          this.defenseGarrisonHero.heroId === this.defenseSelectedHeroId &&
          this.defenseGarrisonHero.row === r &&
          this.defenseGarrisonHero.col === c;
        if (sameCell) return false;
        this.defenseDraftCells = sortCells(
          withoutCell(this.defenseDraftCells, r, c)
        );
        this.defenseGarrisonHero = {
          heroId: this.defenseSelectedHeroId,
          row: r,
          col: c,
        };
        this.clampDefensePlaceQty();
        return true;
      }

      const heroHere =
        this.defenseGarrisonHero &&
        this.defenseGarrisonHero.row === r &&
        this.defenseGarrisonHero.col === c;
      if (heroHere) {
        this.defenseGarrisonHero = null;
        if (!this.defenseSelectedSlug) return true;
      }

      const result = placeStack(
        this.defenseDraftCells,
        r,
        c,
        this.defenseSelectedSlug,
        this.defensePlaceQty,
        this.playerUnits,
        this.defenseDraftGateCol
      );
      if (result.changed) {
        this.defenseDraftCells = result.cells;
        this.clampDefensePlaceQty();
      }
      return result.changed;
    },

    clearDefenseCell(row, col) {
      const r = Math.floor(Number(row));
      const c = Math.floor(Number(col));
      if (
        this.defenseGarrisonHero &&
        this.defenseGarrisonHero.row === r &&
        this.defenseGarrisonHero.col === c
      ) {
        this.defenseGarrisonHero = null;
        return true;
      }
      const result = placeStack(
        this.defenseDraftCells,
        r,
        c,
        null,
        0,
        this.playerUnits,
        this.defenseDraftGateCol
      );
      if (result.changed) {
        this.defenseDraftCells = result.cells;
        this.clampDefensePlaceQty();
      }
      return result.changed;
    },

    removeDefenseUnits(row, col, qty) {
      const r = Math.floor(Number(row));
      const c = Math.floor(Number(col));
      if (
        this.defenseGarrisonHero &&
        this.defenseGarrisonHero.row === r &&
        this.defenseGarrisonHero.col === c
      ) {
        this.defenseGarrisonHero = null;
        return true;
      }
      const current = findCell(this.defenseDraftCells, r, c);
      const n = Math.floor(Number(qty) || 0);
      if (!current || n < 1) return false;
      const nextQty = Math.floor(Number(current.quantity) || 0) - n;
      if (nextQty <= 0) {
        this.defenseDraftCells = withoutCell(this.defenseDraftCells, row, col);
      } else {
        this.defenseDraftCells = sortCells(
          withoutCell(this.defenseDraftCells, row, col).concat([
            {
              row: current.row,
              col: current.col,
              unit_slug: current.unit_slug,
              quantity: nextQty,
            },
          ])
        );
      }
      this.clampDefensePlaceQty();
      return true;
    },

    applyDefenseFormation(payload, { draft = true } = {}) {
      const parsed = parseFormationPayload(payload);
      this.cityDefenseCells = parsed.cells;
      this.cityDefenseGateCol = parsed.gate_col;
      this.cityDefenseGarrisonHero = parsed.garrisonHero;
      if (draft) {
        this.defenseDraftCells = cloneCells(parsed.cells);
        this.defenseDraftGateCol = parsed.gate_col;
        this.defenseGarrisonHero = parsed.garrisonHero
          ? { ...parsed.garrisonHero }
          : null;
      }
      return parsed;
    },

    async fetchCityDefense(cityId) {
      if (!cityId) {
        this.cityDefenseCells = [];
        this.cityDefenseGateCol = DEFAULT_GATE_COL;
        this.cityDefenseGarrisonHero = null;
        this.defenseDraftCells = [];
        this.defenseDraftGateCol = DEFAULT_GATE_COL;
        this.defenseGarrisonHero = null;
        return [];
      }
      try {
        const response = await api.get(`/api/progression/city/${cityId}/defense`);
        const uiStore = useUiStore();
        this.applyDefenseFormation(response.data, {
          draft: !uiStore.isEditingDefense,
        });
      } catch (error) {
        console.error("Failed to fetch city defense formation:", error);
        this.cityDefenseCells = [];
        this.cityDefenseGateCol = DEFAULT_GATE_COL;
        this.cityDefenseGarrisonHero = null;
        if (!useUiStore().isEditingDefense) {
          this.defenseDraftCells = [];
          this.defenseDraftGateCol = DEFAULT_GATE_COL;
          this.defenseGarrisonHero = null;
        }
      }
      return this.cityDefenseCells;
    },

    async saveCityDefense(payload) {
      const cityId = this.activeCity?.id;
      if (!cityId) {
        throw new Error("No active city.");
      }
      const source =
        payload && typeof payload === "object"
          ? payload
          : {
              cells: this.defenseDraftCells,
              gate_col: this.defenseDraftGateCol,
              garrisonHero: this.defenseGarrisonHero,
            };
      const gate_col = parseGateCol(
        source.gate_col != null ? source.gate_col : this.defenseDraftGateCol
      );
      const cells = parseCellsPayload(source, gate_col);
      const garrisonHero = parseGarrisonHero(
        source.garrisonHero != null
          ? source.garrisonHero
          : this.defenseGarrisonHero,
        cells,
        gate_col
      );
      this.isSavingDefense = true;
      this.defenseSaveError = "";
      try {
        const response = await api.put(`/api/progression/city/${cityId}/defense`, {
          cells,
          gate_col,
          garrisonHero,
        });
        const saved = this.applyDefenseFormation(response.data, { draft: true });
        this.isMovingGate = false;
        this.defenseSelectedHeroId = null;
        this.clampDefensePlaceQty();
        if (response.data?.garrisonAssigned) {
          this.garrisonAssigned = normalizeUnitMap(response.data.garrisonAssigned);
        }
        return saved;
      } catch (error) {
        const message =
          error.response?.data?.message ||
          error.message ||
          "Failed to save defense formation.";
        this.defenseSaveError = message;
        throw new Error(message);
      } finally {
        this.isSavingDefense = false;
      }
    },

    selectBattleTarget(id) {
      const n = Number(id);
      this.selectedBattleTargetId = Number.isFinite(n) ? n : null;
    },

    clampBattlePlaceQty() {
      const max = Number(this.battleSliderMax) || 0;
      if (max < 1) {
        this.battlePlaceQty = 0;
        return;
      }
      const n = Math.floor(Number(this.battlePlaceQty) || 0);
      this.battlePlaceQty = Math.min(max, Math.max(1, n));
    },

    setBattleSelectedSlug(slug) {
      const key = slug ? String(slug).toLowerCase() : null;
      this.battleSelectedSlug = key && UNIT_SLUGS.includes(key) ? key : null;
      if (this.battleSelectedSlug) this.battleSelectedHeroId = null;
      this.clampBattlePlaceQty();
    },

    setBattleSelectedHeroId(heroId) {
      const n = Math.floor(Number(heroId));
      if (!Number.isFinite(n) || n < 1) {
        this.battleSelectedHeroId = null;
        return;
      }
      this.battleSelectedHeroId = this.battleSelectedHeroId === n ? null : n;
      if (this.battleSelectedHeroId) this.battleSelectedSlug = null;
    },

    setBattlePlaceQty(qty) {
      this.battlePlaceQty = Math.floor(Number(qty) || 0);
      this.clampBattlePlaceQty();
    },

    placeBattleStack(row, col) {
      const r = Math.floor(Number(row));
      const c = Math.floor(Number(col));
      if (!isAttackerCell(r, c)) return false;

      if (this.battleSelectedHeroId) {
        const sameCell =
          this.battleDeployedHero &&
          this.battleDeployedHero.heroId === this.battleSelectedHeroId &&
          this.battleDeployedHero.row === r &&
          this.battleDeployedHero.col === c;
        if (sameCell) return false;
        this.battleAttackCells = sortCells(withoutCell(this.battleAttackCells, r, c));
        this.battleDeployedHero = {
          heroId: this.battleSelectedHeroId,
          row: r,
          col: c,
        };
        this.clampBattlePlaceQty();
        return true;
      }

      const heroHere =
        this.battleDeployedHero &&
        this.battleDeployedHero.row === r &&
        this.battleDeployedHero.col === c;
      if (heroHere) {
        this.battleDeployedHero = null;
        if (!this.battleSelectedSlug) return true;
      }

      const result = placeAttackStack(
        this.battleAttackCells,
        r,
        c,
        this.battleSelectedSlug,
        this.battlePlaceQty,
        this.battleIdleUnits
      );
      if (result.changed) {
        this.battleAttackCells = result.cells;
        this.clampBattlePlaceQty();
      }
      return result.changed;
    },

    removeBattleUnits(row, col, qty) {
      const r = Math.floor(Number(row));
      const c = Math.floor(Number(col));
      if (
        this.battleDeployedHero &&
        this.battleDeployedHero.row === r &&
        this.battleDeployedHero.col === c
      ) {
        this.battleDeployedHero = null;
        return true;
      }
      const result = peelAttackStack(
        this.battleAttackCells,
        r,
        c,
        qty != null ? qty : this.battlePlaceQty
      );
      if (result.changed) {
        this.battleAttackCells = result.cells;
        this.clampBattlePlaceQty();
      }
      return result.changed;
    },

    resetBattleSetup() {
      this.battleAttackCells = [];
      this.battleSelectedSlug = null;
      this.battlePlaceQty = 1;
      this.battleSelectedHeroId = null;
      this.battleDeployedHero = null;
      this.battleStartError = "";
    },

    async startBattle() {
      if (this.isStartingBattle) return null;
      const targetUserId = Number(this.selectedBattleTargetId);
      if (!Number.isFinite(targetUserId)) {
        throw new Error("Select a target before marching.");
      }
      if (!this.battleAttackCells.length) {
        throw new Error("Deploy at least one troop before marching.");
      }
      this.isStartingBattle = true;
      this.battleStartError = "";
      try {
        const payload = {
          targetUserId,
          attackerFormation: this.battleAttackCells,
        };
        if (this.battleDeployedHero) {
          payload.deployedHero = this.battleDeployedHero;
        }
        const response = await api.post("/api/battle/start", payload);
        await Promise.all([
          this.fetchPlayerResources(),
          this.fetchWorldIntel(),
          this.fetchWorldMap(),
        ]);
        return response.data;
      } catch (error) {
        const data = error.response?.data || {};
        const message =
          data.message ||
          data.error ||
          error.message ||
          "Failed to start battle.";
        this.battleStartError = message;
        if (error.response?.status === 409) {
          try {
            await Promise.all([
              this.fetchWorldMap(),
              this.fetchPlayerResources(),
            ]);
          } catch {
            /* banner can still show the 409 message */
          }
        }
        throw new Error(message);
      } finally {
        this.isStartingBattle = false;
      }
    },

    async socketCityCard(cityId, nodeKey, playerCardId) {
      const response = await api.post("/api/progression/city/socket", {
        cityId,
        nodeKey,
        playerCardId,
      });
      if (Array.isArray(response.data?.sockets)) {
        this.citySockets = response.data.sockets;
      } else {
        await this.fetchCitySockets(cityId);
      }
      await this.fetchPlayerCards();
      if (response.data?.rates) {
        this.cities = this.cities.map((city) =>
          String(city.id) === String(cityId)
            ? {
                ...city,
                rates: mergeNestedRates(emptyNestedRates(), response.data.rates),
              }
            : city
        );
      }
      await this.fetchPlayerResources();
      return response.data;
    },

    async unsocketCityCard(cityId, nodeKey) {
      const response = await api.post("/api/progression/city/unsocket", {
        cityId,
        nodeKey,
      });
      if (Array.isArray(response.data?.sockets)) {
        this.citySockets = response.data.sockets;
      } else {
        await this.fetchCitySockets(cityId);
      }
      await this.fetchPlayerCards();
      if (response.data?.rates) {
        this.cities = this.cities.map((city) =>
          String(city.id) === String(cityId)
            ? {
                ...city,
                rates: mergeNestedRates(emptyNestedRates(), response.data.rates),
              }
            : city
        );
      }
      await this.fetchPlayerResources();
      return response.data;
    },

    async fetchHeroSockets(heroId) {
      if (!heroId) {
        this.heroSockets = [];
        return [];
      }
      try {
        const response = await api.get(`/api/progression/hero/${heroId}/sockets`);
        this.heroSockets = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        console.error("Failed to fetch hero sockets:", error);
        this.heroSockets = [];
      }
      return this.heroSockets;
    },

    async fetchAllHeroSockets() {
      const ids = (this.playerHeroes || [])
        .map((hero) => hero.id)
        .filter((id) => Number.isFinite(Number(id)));
      if (!ids.length) {
        this.heroSockets = [];
        return [];
      }
      const lists = await Promise.all(
        ids.map(async (heroId) => {
          try {
            const response = await api.get(
              `/api/progression/hero/${heroId}/sockets`
            );
            return Array.isArray(response.data) ? response.data : [];
          } catch (error) {
            console.error("Failed to fetch hero sockets:", error);
            return [];
          }
        })
      );
      this.heroSockets = lists.flat();
      return this.heroSockets;
    },

    async socketHeroCard(heroId, nodeKey, playerCardId) {
      const response = await api.post("/api/progression/hero/socket", {
        heroId,
        nodeKey,
        playerCardId,
      });
      if (Array.isArray(response.data?.sockets)) {
        this.heroSockets = response.data.sockets;
      } else {
        await this.fetchHeroSockets(heroId);
      }
      await this.fetchPlayerCards();
      return response.data;
    },

    async unsocketHeroCard(heroId, nodeKey) {
      const response = await api.post("/api/progression/hero/unsocket", {
        heroId,
        nodeKey,
      });
      if (Array.isArray(response.data?.sockets)) {
        this.heroSockets = response.data.sockets;
      } else {
        await this.fetchHeroSockets(heroId);
      }
      await this.fetchPlayerCards();
      return response.data;
    },

    async fetchWorldMap() {
      const firstLoad = !Array.isArray(this.worldMap) || this.worldMap.length === 0;
      if (firstLoad) {
        this.isWorldMapLoading = true;
      }
      this.worldMapError = null;
      try {
        const response = await api.get("/api/world/map");
        this.worldMap = Array.isArray(response.data?.empires)
          ? response.data.empires
          : [];
        if (this.selectedWorldEmpire) {
          const selectedId = Number(this.selectedWorldEmpire.id);
          const next = this.worldMap.find(
            (empire) => Number(empire.id) === selectedId
          );
          this.selectedWorldEmpire = next || this.selectedWorldEmpire;
        }
        return this.worldMap;
      } catch (error) {
        console.error(
          "Failed to fetch world map:",
          error.response?.data || error
        );
        this.worldMap = [];
        this.worldMapError =
          error.response?.data?.message || "Failed to load world map.";
        throw error;
      } finally {
        this.isWorldMapLoading = false;
      }
    },

    selectWorldEmpire(empire) {
      this.selectedWorldEmpire = empire || null;
      this.scoutError = "";
    },

    applyScoutTravel(payload) {
      const n = Math.floor(Number(payload?.travelSecondsPerHex));
      this.travelSecondsPerHex = Number.isFinite(n) && n >= 1 ? n : 60;
    },

    async fetchScoutingMissions() {
      try {
        const response = await api.get("/api/world/scout");
        this.scoutingMissions = Array.isArray(response.data?.missions)
          ? response.data.missions
          : [];
        this.applyScoutTravel(response.data);
        return this.scoutingMissions;
      } catch (error) {
        console.error(
          "Failed to fetch scouting missions:",
          error.response?.data || error
        );
        this.scoutingMissions = [];
        throw error;
      }
    },

    async fetchWorldIntel() {
      try {
        const response = await api.get("/api/world/intel");
        this.intelReports = Array.isArray(response.data?.reports)
          ? response.data.reports
          : [];
        this.unreadIntelCount = Math.max(
          0,
          Math.floor(Number(response.data?.unreadCount) || 0)
        );
        return this.intelReports;
      } catch (error) {
        console.error(
          "Failed to fetch world intel:",
          error.response?.data || error
        );
        this.intelReports = [];
        this.unreadIntelCount = 0;
        throw error;
      }
    },

    async fetchBattleReport(id) {
      const n = Math.floor(Number(id));
      if (!Number.isFinite(n) || n < 1) {
        throw new Error("Invalid battle report.");
      }
      const response = await api.get(`/api/battle/${n}`);
      return response.data;
    },

    async startScout(targetUserId, scoutCount) {
      this.isScouting = true;
      this.scoutError = "";
      try {
        const response = await api.post("/api/world/scout", {
          targetUserId,
          scoutCount,
        });
        const remaining = Math.floor(Number(response.data?.remainingScouts));
        if (Number.isFinite(remaining)) {
          this.playerUnits = {
            ...this.playerUnits,
            scout: Math.max(0, remaining),
          };
        }
        if (response.data?.mission) {
          this.scoutingMissions = [
            ...this.scoutingMissions,
            response.data.mission,
          ];
        } else {
          await this.fetchScoutingMissions();
        }
        this.applyScoutTravel(response.data);
        return response.data;
      } catch (error) {
        this.scoutError =
          error.response?.data?.message || "Failed to send scouts.";
        throw error;
      } finally {
        this.isScouting = false;
      }
    },

    async markIntelRead(id) {
      try {
        const response = await api.post(`/api/world/intel/${id}/read`);
        if (Array.isArray(response.data?.reports)) {
          this.intelReports = response.data.reports;
          this.unreadIntelCount = Math.max(
            0,
            Math.floor(Number(response.data?.unreadCount) || 0)
          );
        }
        return response.data?.report || null;
      } catch (error) {
        console.error(
          "Failed to mark intel read:",
          error.response?.data || error
        );
        throw error;
      }
    },

    async fetchMarketPrices() {
      try {
        const response = await api.get("/api/market/prices");
        this.marketPrices = Array.isArray(response.data?.prices)
          ? response.data.prices
          : [];
        return this.marketPrices;
      } catch (error) {
        console.error("Failed to fetch market prices:", error.response?.data || error);
        throw error;
      }
    },

    applyMarketTrade(payload) {
      if (Array.isArray(payload?.prices)) {
        this.marketPrices = payload.prices;
      }
      this.mergeEmpireStocks(payload?.resources);
    },

    async marketBuy({ slug, quantity }) {
      const response = await api.post("/api/market/buy", {
        resource_slug: slug,
        quantity,
      });
      this.applyMarketTrade(response.data);
      return response.data;
    },

    async marketSell({ slug, quantity }) {
      const response = await api.post("/api/market/sell", {
        resource_slug: slug,
        quantity,
      });
      this.applyMarketTrade(response.data);
      return response.data;
    },

    // Fetches current resources (and triggers Lazy Sync on backend)
    async fetchPlayerResources() {
      try {
        const response = await api.get("/api/player/resources");
        this.applyResourcePayload(response.data);
      } catch (error) {
        console.error("Failed to fetch player resources:", error);
        this.cities = [];
        this.globalAggregation = emptyAggregation();
        this.playerUnits = emptyUnitMap();
        this.garrisonAssigned = emptyUnitMap();
      }
    },

    // Re-anchor production with server only when a full tick period is due.
    async claimResources() {
      if (this.isClaiming) return null;
      if (Date.now() < this.claimCooldownUntil) return null;
      if (this.msUntilNextTick > 0) return null;

      this.isClaiming = true;
      const prevTick = tickStampMs(this.globalAggregation?.last_ticked_at);
      try {
        const clientLastTickedAt = this.globalAggregation?.last_ticked_at || null;
        const response = await api.post("/api/player/resources/claim", {
          clientLastTickedAt,
        });
        this.applyResourcePayload(response.data);
        this.applyClaimCooldown(prevTick);
        return response.data;
      } catch (error) {
        if (error.response?.status === 409 && error.response?.data) {
          this.applyResourcePayload(error.response.data);
          this.applyClaimCooldown(prevTick);
          return error.response.data;
        }
        console.error("Failed to claim player resources:", error);
        throw error;
      } finally {
        this.isClaiming = false;
      }
    },

    applyClaimCooldown(prevTickMs) {
      const nextTick = tickStampMs(this.globalAggregation?.last_ticked_at);
      if (nextTick != null && prevTickMs != null && nextTick !== prevTickMs) {
        this.claimCooldownUntil = 0;
        return;
      }
      const remaining = Number(this.msUntilNextTick) || 0;
      this.claimCooldownUntil = Date.now() + Math.max(CLAIM_COOLDOWN_FLOOR_MS, remaining);
    },

    // Fetches Hero Progression Map (Template or Saved)
    async fetchHeroProgression() {
      this.isLoading = true;
      try {
        if (!this.activeHero) await this.fetchPlayerAssets();

        // 1. Get visual layout from JSON
        const mapTemplate = normalizeMapData(heroMapData[0]);

        // 2. Get user unlock state from Database
        if (this.activeHero) {
          const response = await api.get(`/api/progression/hero/${this.activeHero.id}`);
          const unlockedKeys = response.data || []; // Array of node_key strings from DB

          mapTemplate.nodes.forEach((node) => {
            // Cross-reference JSON key with DB keys
            node.isUnlocked = unlockedKeys.includes(node.node_key);
          });
        }
        this.heroProgression = mapTemplate;
      } catch (error) {
        console.error("Failed hero progression:", error);
      } finally {
        this.isLoading = false;
      }
    },

    // Fetches City Progression Map (Template or Saved)
    async fetchCityProgression() {
      this.isLoading = true;
      try {
        if (!this.activeCity) await this.fetchPlayerAssets();

        // 1. Get visual layout from JSON
        const mapTemplate = normalizeMapData(cityMapData[0]);

        // 2. Get user unlock state from Database
        if (this.activeCity) {
          const response = await api.get(`/api/progression/city/${this.activeCity.id}`);
          const unlockedKeys = response.data || []; // Array of node_key strings from DB

          mapTemplate.nodes.forEach((node) => {
            // Cross-reference JSON key with DB keys
            node.isUnlocked = unlockedKeys.includes(node.node_key);
          });
        }
        this.cityProgression = mapTemplate;
      } catch (error) {
        console.error("Failed city progression:", error);
      } finally {
        this.isLoading = false;
      }
    },

    mergeResourceBalances(resourcesRow) {
      this.applyResourcePayload(resourcesRow);
    },

    // --- Unlock a REGULAR City Node ---
    async unlockCityNode(nodeKey) {
      console.log("GameStore: unlockCityNode called.");
      console.log("Current activeCity:", this.activeCity);
      if (!this.activeCity) {
        console.error("GameStore: No activeCity set!");
        throw new Error("No active city found.");
      }
      if (!nodeKey) throw new Error("Node key is required.");

      this.isLoading = true;
      try {
        const response = await api.post(
          `/api/progression/city/${this.activeCity.id}/unlock`,
          { nodeKey }
        );
        this.mergeResourceBalances(response.data.resources);
        await this.fetchCityProgression();
        await this.fetchPlayerResources(); // refresh production rates after unlock
        return { success: true, message: response.data.message };
      } catch (error) {
        console.error("Error unlocking city node:", error);
        throw new Error(
          error.response?.data?.message || "Server error unlocking node."
        );
      } finally {
        this.isLoading = false;
      }
    },

    // --- Unlock a REGULAR Hero Node ---
    async unlockHeroNode(nodeKey) {
      if (!this.activeHero) throw new Error("No active hero found.");
      if (!nodeKey) throw new Error("Node key is required.");

      this.isLoading = true;
      try {
        const response = await api.post(
          `/api/progression/hero/${this.activeHero.id}/unlock`,
          { nodeKey }
        );
        this.mergeResourceBalances(response.data.resources);
        await this.fetchHeroProgression();
        return { success: true, message: response.data.message };
      } catch (error) {
        console.error("Error unlocking hero node:", error);
        throw new Error(
          error.response?.data?.message || "Server error unlocking node."
        );
      } finally {
        this.isLoading = false;
      }
    },

    // --- Found a NEW City ---
    async foundCity(nodeKey, cityName) {
      if (!nodeKey || nodeKey !== "FoundCity")
        throw new Error("Invalid node key for founding city.");
      const name =
        typeof cityName === "string"
          ? cityName.trim().replace(/ +/g, " ")
          : "";
      if (!/^[A-Za-z0-9]+(?: [A-Za-z0-9]+)*$/.test(name) || name.length > 50) {
        throw new Error(
          "City name must be alphanumeric. Spaces are allowed."
        );
      }

      this.isLoading = true;
      try {
        const response = await api.post(`/api/player/found-city`, {
          nodeKey,
          cityName: name,
        });
        this.mergeResourceBalances(response.data.resources);
        this.playerCities.push(response.data.city);
        this.activeCity = response.data.city;
        this.justFoundedCity = true;
        await this.fetchCityProgression();
        await this.fetchPlayerResources();
        return { success: true, message: response.data.message };
      } catch (error) {
        console.error("Error founding city:", error);
        throw new Error(
          error.response?.data?.message || "Server error founding city."
        );
      } finally {
        this.isLoading = false;
      }
    },

    // --- Activate first hero (requires alphanumeric name) ---
    async activateHero(nodeKey, heroName) {
      if (!nodeKey) throw new Error("Invalid node key...");
      const name = typeof heroName === "string" ? heroName.trim() : "";
      if (!/^[A-Za-z0-9]+$/.test(name)) {
        throw new Error("Hero name must be alphanumeric with no spaces.");
      }

      this.isLoading = true;
      try {
        const response = await api.post(`/api/player/activate-hero`, {
          nodeKey,
          heroName: name,
        });
        this.mergeResourceBalances(response.data.resources);
        this.playerHeroes.push(response.data.hero);
        this.activeHero = response.data.hero;
        this.justActivatedHero = true;
        await this.fetchHeroProgression();
        return { success: true, message: response.data.message };
      } catch (error) {
        console.error("Error activating hero:", error);
        throw new Error(
          error.response?.data?.message || "Server error activating hero."
        );
      } finally {
        this.isLoading = false;
      }
    },


    setFocusedQuestSlug(slug) {
      this.focusedQuestSlug = slug || null;
    },

    setQuestTroopQty(questSlug, unitSlug, qty) {
      if (!questSlug || !UNIT_SLUGS.includes(unitSlug)) return;
      const remaining = this.remainingUnitsForQuest(questSlug);
      const nextQty = Math.max(
        0,
        Math.min(remaining[unitSlug] || 0, Math.floor(Number(qty) || 0))
      );
      const current = this.questAssignments[questSlug] || {
        assigned_hero_id: null,
        assigned_troops: {},
      };
      const troops = { ...(current.assigned_troops || {}) };
      if (nextQty < 1) {
        delete troops[unitSlug];
      } else {
        troops[unitSlug] = nextQty;
      }
      this.questAssignments = {
        ...this.questAssignments,
        [questSlug]: { ...current, assigned_troops: troops },
      };
    },

    assignUnitToQuest(questSlug, payload) {
      if (!questSlug || !payload || typeof payload !== "object") return;
      const isActiveMission = this.activeQuests.some(
        (q) => (q.slug || q.quest_slug) === questSlug
      );
      if (isActiveMission) return;

      if (payload.type === "hero") {
        const heroId = payload.id;
        if (!heroId) return;
        if (this.inProgressHeroIds.has(heroId)) return;

        const next = { ...this.questAssignments };
        for (const slug of Object.keys(next)) {
          if (slug !== questSlug && next[slug]?.assigned_hero_id === heroId) {
            next[slug] = { ...next[slug], assigned_hero_id: null };
          }
        }
        const current = next[questSlug] || {
          assigned_hero_id: null,
          assigned_troops: {},
        };
        next[questSlug] = { ...current, assigned_hero_id: heroId };
        this.questAssignments = next;
        return;
      }

      if (payload.type === "troop") {
        const unitSlug = payload.slug;
        if (!unitSlug || !UNIT_SLUGS.includes(unitSlug)) return;
        const quest = this.availableQuests.find((q) => q.slug === questSlug);
        if (quest && quest.allows_troops === false) return;
        const current = this.questAssignments[questSlug] || {
          assigned_hero_id: null,
          assigned_troops: {},
        };
        const currentQty = Math.floor(Number(current.assigned_troops?.[unitSlug]) || 0);
        const add = Number(payload.quantity) > 0 ? Number(payload.quantity) : 1;
        this.setQuestTroopQty(questSlug, unitSlug, currentQty + add);
      }
    },

    clearQuestAssignment(questSlug, field) {
      if (!questSlug) return;
      const current = this.questAssignments[questSlug];
      if (!current) return;

      if (field === "hero") {
        this.questAssignments = {
          ...this.questAssignments,
          [questSlug]: { ...current, assigned_hero_id: null },
        };
        return;
      }
      if (field === "troops") {
        this.questAssignments = {
          ...this.questAssignments,
          [questSlug]: { ...current, assigned_troops: {} },
        };
        return;
      }

      const next = { ...this.questAssignments };
      delete next[questSlug];
      this.questAssignments = next;
    },

    // --- Quests ---
    async fetchAvailableQuests() {
      try {
        const response = await api.get("/api/quests/available");
        const data = response.data || {};
        this.availableQuests = data.quests || [];
        return data;
      } catch (error) {
        console.error("Failed to fetch available quests:", error);
        this.availableQuests = [];
        return { quests: [], hasHero: false };
      }
    },

    async fetchActiveQuests() {
      try {
        const response = await api.get("/api/quests/active");
        this.activeQuests = response.data;
      } catch (error) {
        console.error("Failed to fetch active quests:", error);
      }
    },

    // --- Quest Actions ---
    async startQuest(payload) {
      if (!payload.questSlug) throw new Error("Quest Slug required");
      const assigned_hero_id = payload.assigned_hero_id || null;
      const assigned_troops = payload.assigned_troops || {};
      this.isLoading = true;
      try {
        const response = await api.post("/api/quests/start", {
          questSlug: payload.questSlug,
          addedGold: payload.addedGold || 0,
          assigned_hero_id,
          assigned_troops,
        });
        
        // Optimistic / Response update
        // Remove from available
        this.availableQuests = this.availableQuests.filter(q => q.slug !== payload.questSlug);
        this.clearQuestAssignment(payload.questSlug);
        if (this.focusedQuestSlug === payload.questSlug) {
          this.focusedQuestSlug = null;
        }
        
        // Add to active (or just refetch)
        // Ideally we push the new active quest object
        if (response.data.quest) {
            // We need to fetch the full object or just push what we have. 
            // The backend returns the active_quest record.
            // We might need to refetch active quests to get the joined data (title, desc, etc)
            await this.fetchActiveQuests();
        }
        
        // Update resources (backend already deducted, but we need new state)
        await this.fetchPlayerResources();
        
        return { success: true };
      } catch (error) {
        console.error("Error starting quest:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    async claimQuest(activeQuestId) {
      this.isLoading = true;
      // Lazy import to avoid circular dep if any (though stores are usually fine)
      const { useNotificationStore } = await import("./notifications");
      const notificationStore = useNotificationStore();

      try {
        const response = await api.post("/api/quests/claim", { activeQuestId });
        const { isSuccess, manpowerRefund, rewards, processedRewards, leveledUp, newLevel, message } = response.data;

        // Show Notification
        notificationStore.addNotification(message, isSuccess ? 'success' : 'alert');
        
        // Show additional level-up notification if applicable
        if (leveledUp && newLevel) {
          notificationStore.addNotification(`🎉 Congratulations! You reached Level ${newLevel}!`, 'success');
        }

        // Update State - Full Sync
        this.activeQuests = this.activeQuests.filter(q => q.id !== activeQuestId);
        
        await Promise.all([
          this.fetchPlayerResources(),
          this.fetchPlayerAssets({ force: true }),
          this.fetchAvailableQuests(),
          this.fetchPlayerPacks(),
        ]);

        return response.data;
      } catch (error) {
        console.error("Error claiming quest:", error);
        notificationStore.addNotification(error.response?.data?.error || "Failed to claim quest", "alert");
        throw error;
      } finally {
        this.isLoading = false;
      }
    }
  },
});
